
import React, { useEffect } from "react";
import { useContentProcessor } from "@/hooks/content-processor";
import LoadingState from "./LoadingState";
import ContentRenderer from "./ContentRenderer";
import PremiumContentOverlay from "./PremiumContentOverlay";
import ErrorState from "./ErrorState";
import { isProductionBuild, isViteAssetReference, shouldBypassContentProcessing, cleanupHomePageContent } from "@/utils/productionFallbacks";

interface ArticleContentProps {
  isLoading: boolean;
  content: string;
  showPreview: boolean;
  /**
   * When true, allow users to read a portion of the article before
   * showing the subscribe overlay.
   */
  isPreviewMode?: boolean;
}

const ArticleContent = ({ isLoading, content, showPreview, isPreviewMode = false }: ArticleContentProps) => {
  // Get the current article slug from the URL
  const slug = window.location.pathname.split('/articles/')[1];
  const isProduction = isProductionBuild();
  const isHomepage = shouldBypassContentProcessing();
  const isPreview = window.location.hostname.includes('preview--');

  useEffect(() => {
    if (isHomepage) {
      console.log("ArticleContent: On homepage - bypassing content processing");
      return;
    }
    
    if (isLoading) {
      console.log("ArticleContent: Content is loading");
    }
    
    // Add debugging for preview environment
    if (isPreview) {
      console.log("ArticleContent: Running in preview environment", {
        hostname: window.location.hostname,
        content: content ? content.substring(0, 100) : "No content",
        slug
      });
    }
    
    // Add extra logging for debugging Vite references on homepage
    if (isHomepage && content && content.includes('export default')) {
      console.log("ArticleContent: IMPORTANT - Found export default on homepage, content:", content);
    }
  }, [isLoading, isHomepage, content, isPreview, slug]);

  // HOMEPAGE SPECIAL CASE: Always return empty div for homepage without any content processing
  if (isHomepage) {
    console.log("ArticleContent: On homepage - rendering empty content container");
    return <div className="homepage-content"></div>;
  }

  // Clean up content for homepage in production
  const cleanedContent = isHomepage ? cleanupHomePageContent(content) : content;
  
  // Handle raw Vite export reference in preview environment
  if (isPreview && cleanedContent && cleanedContent.includes('export default')) {
    console.log("ArticleContent: Preview environment detected raw export reference");
    
    return (
      <div className="p-6 bg-amber-100 border border-amber-300 rounded-lg">
        <h2 className="text-2xl font-semibold mb-3">Preview Mode</h2>
        <p className="mb-4">You are viewing this site in preview mode. Some content may not be available until the site is fully deployed.</p>
        <p className="text-sm text-gray-600">Try viewing the live site instead of the preview.</p>
      </div>
    );
  }
  
  // Use our custom hook to process the content (only for non-homepage routes)
  const { processedContent, isLoading: processingContent, error } = useContentProcessor(cleanedContent, { slug });
  
  // Special handling for Vite asset references in production
  if (isViteAssetReference(content)) {
    console.log("ArticleContent - Vite asset reference detected:", content);
    // For homepage with Vite reference, render empty div
    if (isHomepage) {
      console.log("ArticleContent - Homepage with Vite reference, rendering empty div");
      return <div className="homepage-content"></div>;
    }
    
    // For preview environment with Vite reference, show helpful message
    if (isPreview) {
      return (
        <div className="p-6 bg-amber-100 border border-amber-300 rounded-lg">
          <h2 className="text-2xl font-semibold mb-3">Preview Mode</h2>
          <p className="mb-4">You are viewing this site in preview mode. Some content may not be available until the site is fully deployed.</p>
          <p className="text-sm text-gray-600">Article ID: {slug}</p>
        </div>
      );
    }
  }

  // Handle loading state - Important: Show loading state during BOTH initial loading AND content processing
  if (isLoading || processingContent) {
    return <LoadingState />;
  }

  // IMPORTANT CHANGE: Only show error state when we're definitely not loading AND have an error or no content
  // This prevents the brief flash of the error state while content is still loading
  if ((error || !content) && !isLoading && !processingContent) {
    // For homepage, just render an empty div on error
    if (isHomepage) {
      console.log("ArticleContent: On homepage with error, rendering empty content");
      return <div className="homepage-content"></div>;
    }
    
    // For preview environment, show a more helpful message
    if (isPreview) {
      return (
        <div className="p-6 bg-amber-100 border border-amber-300 rounded-lg">
          <h2 className="text-2xl font-semibold mb-3">Preview Mode</h2>
          <p className="mb-4">You are viewing this site in preview mode. Some content may not be available until the site is fully deployed.</p>
          <p className="text-sm text-gray-600">Error: {error || "Content not available in preview"}</p>
        </div>
      );
    }
    
    return <ErrorState message={error || "No content available. Please try refreshing the page."} />;
  }

  // Determine if content is markdown
  const processedContentString = processedContent ? String(processedContent) : "";
  const isMarkdown = processedContentString.trim().startsWith('#') || false;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const [showOverlay, setShowOverlay] = React.useState(false);

  // When in preview mode, show overlay once user scrolls past 20% of the article
  useEffect(() => {
    if (!isPreviewMode) return;
    const container = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const updateSentinelPosition = () => {
      const height = container.scrollHeight;
      sentinel.style.top = `${height * 0.2}px`;
    };

    updateSentinelPosition();
    const resizeObserver = new ResizeObserver(updateSentinelPosition);
    resizeObserver.observe(container);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setShowOverlay(true);
        }
      });
    });
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [isPreviewMode, processedContentString]);
  
  return (
    <div className="relative" ref={containerRef}>
      <div className="article-content leading-relaxed">
        <ContentRenderer
          content={processedContentString}
          isMarkdown={isMarkdown}
          slug={slug}
        />
      </div>

      {isPreviewMode && <div ref={sentinelRef} className="absolute left-0 w-full h-px" />}
      {(showPreview || (isPreviewMode && showOverlay)) && (
        <PremiumContentOverlay />
      )}
    </div>
  );
}; 

export default ArticleContent;
